import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Userpilot } from 'userpilot';

import { setDash0UserContext } from '~/common/helpers/dash0/initDash0';
import {
  RECAPTCHA_SITE_KEY,
  PORTAL_FULLSTORY_ORG_ID,
  USEPILOT_KEY,
  ACTIVATE_POLLING_PERMISSONS,
  POLLING_PERMISSIONS_INTERVAL,
  subscribeToConfigChanges
} from '~/common/helpers/environment';
import getEnvironmentsFromPathName from '~/common/helpers/getEnvironmentsToPathName';
import getRealmFromPathName from '~/common/helpers/getRealmToPathName';
import WebAnalytics from '~/common/helpers/webAnalytics';
import withHooks from '~/common/hoc/withHooks';
import usePolling from '~/common/hooks/usePolling';

const loadReCaptcha = siteKey => {
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
  document.body.appendChild(script);
};

const enhancer = withHooks(props => {
  const { pathname } = useLocation();
  const location = useLocation();
  const application = useSelector(state => state.application);
  const authentication = useSelector(state => state.authentication);
  const intercom = useSelector(state => state.intercom);
  const dispatch = useDispatch();

  const currentState = {
    isSignedIn: authentication.isSignedIn,
    userData: authentication.userData,
    realm: application.realm.realm,
    hiddenPages: application.hiddenPages || []
  };

  useEffect(() => {
    const unsubscribe = subscribeToConfigChanges(() => {
      if (RECAPTCHA_SITE_KEY && USEPILOT_KEY) {
        loadReCaptcha(RECAPTCHA_SITE_KEY);
        Userpilot.initialize(USEPILOT_KEY);
      }

      if (PORTAL_FULLSTORY_ORG_ID && authentication?.userData) {
        WebAnalytics.init(authentication.userData);
      }
    });

    return unsubscribe;
  }, [authentication?.userData]);

  useEffect(() => {
    performance.mark(location.pathname);
  }, []);

  useEffect(() => {
    if (RECAPTCHA_SITE_KEY && USEPILOT_KEY) {
      loadReCaptcha(RECAPTCHA_SITE_KEY);
      Userpilot.initialize(USEPILOT_KEY);
    }
  }, []);

  useEffect(() => {
    if (authentication?.userData) {
      Userpilot.reload();
    }
  }, [location]);

  useEffect(() => {
    if (authentication?.userData) {
      Userpilot.identify(authentication?.userData.email, {
        realm: authentication?.userData.realm,
        email: authentication?.userData.email,
        lastLogin: authentication?.userData.lastLogin,
        createdAt: authentication?.userData.createdAt,
        updatedAt: authentication?.userData.updatedAt
      });
    }

    if (PORTAL_FULLSTORY_ORG_ID) {
      WebAnalytics.init(authentication?.userData);
    }

    setDash0UserContext(authentication?.userData);
  }, [intercom?.key]);

  useEffect(() => {
    if (currentState.isSignedIn) {
      const { realm } = getRealmFromPathName(pathname);
      const environment = getEnvironmentsFromPathName(pathname);

      if (environment) dispatch.application.setEnvironments(environment);

      if (realm) {
        dispatch.application.setRealm({ realm });

        if (application.activeRealm !== realm) {
          dispatch.application.changeRealm({ realm });
          dispatch.application.getTogaiToken(realm);
        }
      } else if (currentState.userData) {
        const pipelinesPath = `/${currentState.userData.realm}/operation/build/pipelines`;

        dispatch.router.navigate({ to: pipelinesPath });
      }
    } else if (!/(\/login|\/oauth)/.test(pathname)) {
      dispatch.router.navigate({ to: '/login' });
    }
  }, [pathname]);

  usePolling(
    () => dispatch.acls.fetch(),
    POLLING_PERMISSIONS_INTERVAL,
    currentState.userData &&
      currentState.realm &&
      ACTIVATE_POLLING_PERMISSONS === 'true'
  );

  useEffect(() => {
    if (currentState.userData && currentState.realm) {
      dispatch.application.initialization();
    }
  }, [currentState.realm]);

  return {
    ...props,
    ...currentState
  };
});

export default enhancer;
